import React, { useEffect, useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Ansprechpartner } from "@/components/site/Ansprechpartner";
import { Kontakt } from "@/components/site/Kontakt";
import { FlowLines } from "@/components/site/FlowLines";
import { Container, Label, Section } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";
import lexikon from "@/data/lexikon.json";

const TABS = ["Alle", "Projektentwicklung", "Personal & Berufe"];

export default function LexikonIndex() {
  const [tab, setTab] = useState("Alle");

  useEffect(() => {
    document.title = "Energie-Lexikon: Begriffe aus Projektentwicklung & Personal | energyleads";
    const desc = document.querySelector('meta[name="description"]');
    if (desc)
      desc.setAttribute(
        "content",
        "Energie-Lexikon: Begriffe aus Projektentwicklung und Personal — von Batteriespeicher und Netzverknüpfungspunkt bis Elektrofachkraft und Handelsvertreter.",
      );
    window.scrollTo(0, 0);
  }, []);

  const terms = useMemo(
    () =>
      Object.entries(lexikon)
        .filter(([, t]) => tab === "Alle" || t.kategorie === tab)
        .sort((a, b) => a[1].term.localeCompare(b[1].term, "de")),
    [tab],
  );

  const letters = useMemo(() => [...new Set(terms.map(([, t]) => t.term[0].toUpperCase()))], [terms]);
  const groups = letters.map((l) => [l, terms.filter(([, t]) => t.term[0].toUpperCase() === l)]);

  return (
    <>
      <Header />
      <main data-testid="lexikon-main">
        <section className="has-lines relative overflow-hidden bg-[#1e2532] pb-16 pt-[150px] text-white on-dark">
          <FlowLines />
          <Container>
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1.5 text-[13px] text-white/50">
              <a href="/" className="hover:text-white">Startseite</a>
              <ChevronRight size={13} />
              <span className="text-white/80">Lexikon</span>
            </nav>
            <Label>Lexikon</Label>
            <h1 className="mt-7 max-w-[860px] font-bold" data-testid="lexikon-h1">
              Energie-Lexikon: Begriffe aus{" "}
              <span style={{ color: "var(--accent)" }}>Projektentwicklung und Personal</span>
            </h1>
            <p className="mt-6 max-w-[660px] text-[16.5px] leading-[1.72] text-white/72">
              Kurz und praxisnah erklärt: die Begriffe, die in Projekten für Photovoltaik, Batteriespeicher und
              Wärmepumpe täglich fallen — und die Berufsbilder dahinter.
            </p>
            <div className="mt-9 flex flex-wrap gap-2.5" data-testid="lexikon-tabs">
              {TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`lexikon-tab-${t}`}
                  onClick={() => setTab(t)}
                  aria-pressed={tab === t}
                  className={`rounded-full border px-4 py-2.5 text-[14px] font-medium ${
                    tab === t
                      ? "border-[#3ecf9b] bg-[#3ecf9b]/12 text-[#3ecf9b]"
                      : "border-white/15 bg-white/[0.05] text-white/75 hover:border-white/35"
                  }`}
                  style={{ transition: "border-color .3s ease, color .3s ease, background-color .3s ease" }}
                >
                  {t}
                </button>
              ))}
            </div>
          </Container>
        </section>

        <Section testId="lexikon-liste" className="has-lines overflow-hidden bg-[#f5f6f4]">
          <FlowLines color="#0F9D6E" opacity={[0.055, 0.045]} />
          <Container>
            <nav aria-label="Alphabet" className="flex flex-wrap gap-2" data-testid="lexikon-az">
              {letters.map((l) => (
                <a
                  key={l}
                  href={`#buchstabe-${l}`}
                  className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-[#d4d8d5] bg-white text-[14px] font-bold text-[#1e2532] hover:border-[#0f9d6e] hover:text-[#0b7d57]"
                  style={{ transition: "border-color .3s ease, color .3s ease" }}
                >
                  {l}
                </a>
              ))}
            </nav>

            {groups.map(([letter, items]) => (
              <div key={letter} id={`buchstabe-${letter}`} className="mt-14 scroll-mt-[110px]">
                <h2 className="font-display text-[28px] font-bold text-[#0f9d6e]">{letter}</h2>
                <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-6">
                  {items.map(([slug, t]) => (
                    <a
                      key={slug}
                      href={`/lexikon/${slug}/`}
                      data-testid={`lexikon-card-${slug}`}
                      className="card-hover h-full rounded-[16px] border border-[#dfe2e0] bg-white p-6 md:p-7"
                    >
                      <span className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#0b7d57]">
                        {t.kategorie}
                      </span>
                      <h3 className="mt-3 text-[19px] font-semibold leading-[1.3] text-[#1e2532]">{t.term}</h3>
                      <p className="mt-3 text-[15.5px] leading-[1.7] text-[#565f70]">{t.kurz}</p>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </Container>
        </Section>

        <Ansprechpartner />
        <Kontakt />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
